import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { IUniversalDatasetTableConfig } from "components/dashboard/universal-dataset-table/universal-dataset-table-editor/CellValuesEditor";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { T } from "services/lang/LanguageService";
import { Dialog } from "primereact/dialog";
import React from "react";
import useDashboardStore from "store/dashboard-store";
import DraggableItem, { ReorderType } from "./DraggableItem";
import { onDragEndSplice, tabUpdateInDashboard, updateDashboardTabs } from "../../../utils";

interface ReorderProps {
  setReorderDialogVisibility: (reorderTabsDialogVisibility: boolean) => void;
  reorderDialogVisibility: boolean;
  reorderType: ReorderType;
  universalTableConfig?: IUniversalDatasetTableConfig;
  onUniversalTableConfigChange?: (config: IUniversalDatasetTableConfig) => void;
  onChange?: (tabId: number) => void;
  group?: DashboardComponentGroup;
  tabId?: number;
}

const Reorder = (props: ReorderProps) => {
  const dashboard = useDashboardStore(state => state.dashboard);
  const setDashboard = useDashboardStore(state => state.setDashboard);

  const onDragEnd = (result: any) => {
    if (!result.destination || !dashboard) return;

    switch (props.reorderType) {
      case ReorderType.TABS:
        handleReorderTabs(result);
        break;
      case ReorderType.COMPONENTS:
        handleReorderComponents(result);
        break;
      case ReorderType.GROUPS:
        handleReorderGroups(result);
        break;
      case ReorderType.UNIVERSAL_TABLE_COLUMNS:
        handleReorderColumns(result);
        break;
      default:
        break;
    }
  };

  const handleReorderTabs = (result: any) => {
    if (dashboard?.dashboardTabsJs) {
      const reorderedTabs = Array.from(dashboard.dashboardTabsJs);
      const [removed] = reorderedTabs.splice(result.source.index, 1);
      reorderedTabs.splice(result.destination.index, 0, removed);

      setDashboard({ ...dashboard, dashboardTabsJs: reorderedTabs });
      props.onChange?.(0);
    }
  };

  const handleReorderComponents = (result: any) => {
    if (props.tabId == null || props.group?.id == null || !dashboard) return;

    const updatedComponentGroups = dashboard.dashboardTabsJs[props.tabId].componentGroups.map(
      (currentGroup: DashboardComponentGroup) => {
        if (currentGroup.id === props.group?.id) {
          const reorderedComponents = onDragEndSplice(result, currentGroup.components || []);
          return { ...currentGroup, components: reorderedComponents };
        }
        return currentGroup;
      }
    );

    updateDashboardTabs(
      dashboard,
      tabUpdateInDashboard(dashboard.dashboardTabsJs, props.tabId, {
        componentGroups: updatedComponentGroups,
      })
    );
  };

  const handleReorderGroups = (result: any) => {
    if (props.tabId == null || !dashboard) return;

    const reorderedGroups = onDragEndSplice(
      result,
      dashboard.dashboardTabsJs[props.tabId].componentGroups
    );

    updateDashboardTabs(
      dashboard,
      dashboard.dashboardTabsJs.map((tab, index) =>
        index === props.tabId ? { ...tab, componentGroups: reorderedGroups } : tab
      )
    );
  };

  const handleReorderColumns = (result: any) => {
    if (props.universalTableConfig?.columnsOptions) {
      const reorderedColumnsOptions = onDragEndSplice(
        result,
        props.universalTableConfig.columnsOptions
      );

      const reorderedRows = props.universalTableConfig.rows.map(row => {
        const reorderedRowColumns = onDragEndSplice(result, row.columns);
        return { ...row, columns: reorderedRowColumns };
      });

      props.onUniversalTableConfigChange?.({
        ...props.universalTableConfig,
        columnsOptions: reorderedColumnsOptions,
        rows: reorderedRows,
      });
    }
  };

  const reorderTypeHeaders = {
    [ReorderType.TABS]: T("label.reorder-tabs"),
    [ReorderType.GROUPS]: T("label.reorder-groups"),
    [ReorderType.COMPONENTS]: T("label.reorder-components"),
    [ReorderType.UNIVERSAL_TABLE_COLUMNS]: T("label.reorder-columns"),
  };

  const getDroppableContent = () => {
    switch (props.reorderType) {
      case ReorderType.TABS:
        return dashboard?.dashboardTabsJs.map((tab, index) => (
          <Draggable key={tab.uuid} draggableId={tab.uuid} index={index}>
            {provided => <DraggableItem item={tab} provided={provided} type={ReorderType.TABS} />}
          </Draggable>
        ));

      case ReorderType.COMPONENTS:
        return props.group?.components?.map((component, index) => (
          <Draggable key={component.id} draggableId={component.id} index={index}>
            {provided => (
              <DraggableItem item={component} provided={provided} type={ReorderType.COMPONENTS} />
            )}
          </Draggable>
        ));

      case ReorderType.GROUPS:
        return (
          props.tabId != null &&
          dashboard?.dashboardTabsJs[props.tabId].componentGroups.map((group, index) => (
            <Draggable key={group.id} draggableId={group.id} index={index}>
              {provided => (
                <DraggableItem item={group} provided={provided} type={ReorderType.GROUPS} />
              )}
            </Draggable>
          ))
        );

      case ReorderType.UNIVERSAL_TABLE_COLUMNS:
        return props.universalTableConfig?.columnsOptions.map((column, index) => (
          <Draggable key={column.id} draggableId={column.id} index={index}>
            {provided => (
              <DraggableItem
                item={column}
                provided={provided}
                type={ReorderType.UNIVERSAL_TABLE_COLUMNS}
              />
            )}
          </Draggable>
        ));

      default:
        return null;
    }
  };

  return (
    <Dialog
      header={reorderTypeHeaders[props.reorderType]}
      className="w-30rem"
      style={{ maxHeight: "60rem" }}
      modal={false}
      resizable
      draggable
      visible={props.reorderDialogVisibility}
      onHide={() => props.setReorderDialogVisibility(false)}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`${props.group?.id || "droppable"}`} direction="vertical">
          {providedDrop => (
            <div
              {...providedDrop.droppableProps}
              ref={providedDrop.innerRef}
              className="tabsFlex column"
            >
              {getDroppableContent()}
              {providedDrop.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Dialog>
  );
};

export default Reorder;
